import classnames from 'classnames2';
import React, { useEffect, useRef } from 'react';

import { IBaseProps } from '../utils';

import { CheckboxIndicator } from './CheckboxIndicator';

import styles from './Checkbox.module.scss';

export interface ICheckboxProps extends IBaseProps {
  /** Specifies an id for the checkbox input element. */
  id?: string;
  /** Determines if checkbox is checked or has mixed state. */
  checked?: boolean | 'mixed';
  ariaControls?: string[];
  /** Determines if checkbox is checked or has mixed state. */
  ariaLabel?: string;
  /** The text label that describes the purpose of this checkbox option to the user */
  label?: string;
  /** Determines if checkbox is disabled. */
  disabled?: boolean;
  /** Defines the color hex code (ie #000000) used for the checkbox indicator */
  color?: string;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  /** A function that is called whenever the user tries
   * to update the checkbox input control value, except when disabled is set to true
   */
  onChange?(e: React.ChangeEvent<HTMLInputElement>): void;
}

function createCheckbox(displayName, size) {
  const CheckboxBase: React.FC<ICheckboxProps> = (props) => {
    const checkRef = useRef(null);
    useEffect(() => {
      checkRef.current.checked = props.checked === true;
      checkRef.current.indeterminate = props.checked === 'mixed';
    }, [props.checked]);
    return (
      <label
        className={classnames(styles.label, props.className, {
          [styles.labelDefault]: size === 'default',
          [styles.labelSmall]: size === 'small',
          [styles.labelSelected]: props.checked,
          [styles.labelDisabled]: props.disabled
        })}
        data-test={props.dataTest}
      >
        <input
          id={props.id}
          type="checkbox"
          ref={checkRef}
          className={styles.input}
          disabled={props.disabled}
          onChange={props.onChange}
          onBlur={props.onBlur}
          role="checkbox"
          aria-label={props.ariaLabel}
          aria-checked={props.checked}
          aria-controls={props.ariaControls && props.ariaControls.join(' ')}
        />
        <CheckboxIndicator size={size} checked={props.checked} color={props.color ? props.color : null} />
        {props.label}
      </label>
    );
  };

  CheckboxBase.displayName = displayName;

  return CheckboxBase;
}

const DefaultCheckbox = createCheckbox('Checkbox.Default', 'default');

export const Checkbox = Object.assign(DefaultCheckbox, {
  Default: DefaultCheckbox,
  Small: createCheckbox('Checkbox.Small', 'small')
});
